import { cloneDeep } from 'lodash';
import { Portal } from '../portal.model';
import { PortalMenuItem, PortalMenuPreferences } from './../user-preferences/portal-menu-preferences.model';

export class PortalMenuHelper {

    public static organizePortals(
        portals: Portal[],
        preferences?: PortalMenuPreferences,
        lockStudentPortals?: boolean
    ): Portal[] {
        // POST: organizes the portals for the portal menu component
        portals = cloneDeep(portals);
        let newPortals: Portal[] = [];

        // Only process if we have portals
        if (portals && portals.length > 0) {

            // Organizing the portals
            const groupPortals: { [group: string]: Portal } = {};
            const parentPortals: { [parentId: number]: Portal } = {};
            const portalAdded: { [id: number]: boolean } = {};

            // Adding each portal
            // NOTE: the order is important. V9 returns group portals with no description, V10 does not.
            portals
                .filter(portal => portal.ShowAsMenu && !portal.Deleted)
                // eslint-disable-next-line complexity
                .forEach(portal => {
                    portalAdded[portal.Id] = false;

                    // Determining if the portal is a student filtered portal
                    if (
                        (
                            portal.ParentPortal
                            || (
                                portal.GroupName
                                && portal.GroupName.includes('Student')
                            ) || portal.PortalName == 'Academic History'
                        ) && (
                            portal.PortalDescription != null
                            && portal.PortalDescription != ''
                            && !portal.PortalDescription.includes('?studentPortal=true')
                        )
                    ) {
                        portal.PortalDescription += '?studentPortal=true';
                    }

                    // Determining if the portal is an admin portal
                    if (
                        (
                            portal.PortalName == 'Review Behavior Referrals'
                            || portal.PortalName == 'Manage Behavior Referrals'
                        ) && (
                            portal.PortalDescription != null
                            && portal.PortalDescription != ''
                            && !portal.PortalDescription.includes('?adminPortal=true')
                        )
                    ) {
                        portal.PortalDescription += '?adminPortal=true';
                    }

                    // If the portal has a parent, add it to the parent's child array
                    if (portal.ParentId && !portalAdded[portal.Id]) {
                        if (!parentPortals[portal.ParentId]) {
                            parentPortals[portal.ParentId] = portals.find(parentPortal => parentPortal.Id == portal.ParentId);
                            if (parentPortals[portal.ParentId] && !parentPortals[portal.ParentId].ChildPortals) {
                                parentPortals[portal.ParentId].ChildPortals = [];
                            }
                        }
                        if (parentPortals[portal.ParentId]) {
                            parentPortals[portal.ParentId].IsGroup = true;
                            parentPortals[portal.ParentId].ChildPortals.push(portal);
                            portalAdded[portal.Id] = true;
                        }
                    }

                    // If we don't have portals for the group, create them
                    if (portal.GroupName && !portalAdded[portal.Id]) {
                        if (!groupPortals[portal.GroupName]) {
                            const possibleGroupPortals = portals.filter(tempPortal =>
                                tempPortal.PortalName == portal.GroupName
                                && !tempPortal.PortalDescription
                            );
                            if (possibleGroupPortals && possibleGroupPortals.length == 1) {
                                groupPortals[portal.GroupName] = possibleGroupPortals[0];
                                if (!groupPortals[portal.GroupName].ChildPortals) {
                                    groupPortals[portal.GroupName].ChildPortals = [];
                                }
                            } else {
                                groupPortals[portal.GroupName] = new Portal(portal.GroupName);
                                groupPortals[portal.GroupName].IsGroup = true;
                                groupPortals[portal.GroupName].GroupName = portal.GroupName;
                                groupPortals[portal.GroupName].MenuIcon = portal.GroupIcon;
                            }
                        }
                        if (groupPortals[portal.GroupName].Id != portal.Id) {
                            groupPortals[portal.GroupName].ChildPortals.push(portal);
                            portalAdded[portal.Id] = true;
                        }
                    }

                    // Adding the portal if it's not a group (groups will be readded here)
                    // NOTE: We only add portals with portal descriptions here because the others are treated like groups
                    if (!portalAdded[portal.Id] && portal.PortalDescription) {
                        newPortals.push(portal);
                        portalAdded[portal.Id] = true;
                    }

                    // Always make sure they have child portals
                    if (!portal.ChildPortals) {
                        portal.ChildPortals = [];
                    }
                });

            // Helper function for groups
            const addGroup = (groupPortal: Portal) => {
                if (!portalAdded[groupPortal.Id]) {
                    if (groupPortal.ChildPortals.length > 0) {
                        newPortals.push(groupPortal);
                    }
                    portalAdded[groupPortal.Id] = true;
                }
            };

            // Adding all groups
            Object.keys(groupPortals).forEach(groupKey => {
                addGroup(groupPortals[groupKey]);
            });

            // Treat parents that don't have a description as groups
            Object.keys(parentPortals).forEach(parentKey => {
                if (parentPortals[parentKey] && !parentPortals[parentKey].PortalDescription) {
                    addGroup(parentPortals[parentKey]);
                }
            });
        }

        // Sorting the portals
        const sortRecursive = (sortPortals: Portal[]) => {
            sortPortals.sort((a, b) => a.PortalName.localeCompare(b.PortalName));
            sortPortals.forEach(sortPortal => {
                if (sortPortal.ChildPortals && sortPortal.ChildPortals.length > 0) {
                    sortRecursive(sortPortal.ChildPortals);
                }
            });
        };
        sortRecursive(newPortals);

        // Applying the preferences recursively
        const expandRecursive = (expandPortals: Portal[]) => {
            expandPortals.forEach(portal => {
                if (preferences) {
                    const portalMenuPreference = preferences.PortalMenus.filter(preferencePortal =>
                        (
                            !preferencePortal.ParentGroupName || preferencePortal.ParentGroupName.toLowerCase() == portal.GroupName.toLowerCase()
                        )
                        && preferencePortal.GroupName.toLowerCase() == portal.PortalName.toLowerCase()
                        && portal.IsGroup
                    ).sort((a, b) => {
                        if (a.ParentGroupName != null && a.ParentGroupName == portal.GroupName) {
                            if (a.ParentGroupName == b.ParentGroupName && a.Id > b.Id) {
                                return -3;
                            }
                            else {
                                return -2;
                            }
                        }
                        else if (a.ParentGroupName != null) {
                            return -1;
                        }
                        else {
                            return 1;
                        }
                    })[0];

                    if (portalMenuPreference) {
                        portal.Expanded = parseInt(portalMenuPreference.Expanded.toString(), 10);   // NOTE: Expanded is usually a string
                    }
                    else if (portal.IsGroup) {
                        portal.Expanded = 1;
                        const portalMenuItem: PortalMenuItem = {
                            ParentGroupName: portal.GroupName,
                            GroupName: portal.PortalName,
                            GroupIcon: portal.GroupIcon,
                            Expanded: 1,
                            Id: portal.Id
                        };
                        preferences.PortalMenus.push(portalMenuItem);
                    }
                }
                if (portal.ChildPortals && portal.ChildPortals.length > 0) {
                    expandRecursive(portal.ChildPortals);
                }
            });
        };
        expandRecursive(newPortals);

        // If the student portals are locked, only keep preferences for the student portals
        // NOTE: This seems pretty hacky. Shouldn't we just save up the preferences dynamically and overwrite the previous ones?
        if (lockStudentPortals) {
            newPortals = newPortals.filter(p => p.ParentModule == '/student360');
            if (preferences.PortalMenus.filter(m => m.GroupName == 'My Students').length > 0) {
                preferences.PortalMenus = preferences.PortalMenus.filter(m => m.GroupName == 'My Students');
            }
            else {
                preferences.PortalMenus = preferences.PortalMenus.filter(m => m.GroupName == 'Students');
            }
        }

        // Returning the organized portals
        return newPortals;
    }
}